/**
 * useUiPreference — leest/schrijft een view-mode keuze (blokjes/rijen) per
 * admin-pagina. Persistent via PATCH /api/auth/admin/me/preferences.
 *
 * Patroon:
 *   const [view, setView] = useUiPreference("werkgevers_view", "blokjes");
 *   <ViewModeToggle value={view} onChange={setView} />
 *
 * Gedrag:
 *   - Binnen AdminAuthProvider: leest uit useAdminAuth().user.ui_preferences[key].
 *     Bij setView → optimistic update lokale user-state + PATCH naar backend.
 *     Bij failure: revert + toast.error. Server-antwoord is bron van waarheid.
 *   - Buiten AdminAuthProvider (bv. unit-tests die alleen een page mounten):
 *     fallback naar lokale useState — geen API-call, geen crash, maar wel
 *     een console.warn (zodat een vergeten provider in productie zichtbaar
 *     wordt in dev-tools). Fallback is uitsluitend bedoeld voor unit-tests;
 *     productie moet altijd binnen <AdminAuthProvider> renderen.
 */
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { adminPatch, formatApiError } from "./adminApi";
import { AdminAuthContext } from "./adminAuth";

const ALLOWED_KEYS = new Set([
  "werkgevers_view",
  "aanmeetdagen_view",
  "pijn_signalen_view",
  "werknemers_view",
]);
const ALLOWED_VALUES = new Set(["blokjes", "rijen"]);

// Per-key gate zodat dezelfde-key dubbele warning niet bij elke render
// herhaald wordt (één keer per key per browser-sessie is genoeg).
const _missingContextWarned = new Set();

export function useUiPreference(key, defaultValue) {
  const auth = useContext(AdminAuthContext);
  const user = auth?.user ?? null;
  const updateLocalUserPreferences = auth?.updateLocalUserPreferences;

  // Fallback-state voor wanneer er geen auth-provider is (unit-tests). Wordt
  // alleen gebruikt als `auth` ontbreekt — anders is de auth-user-state leidend.
  const [localView, setLocalView] = useState(defaultValue);

  if (!ALLOWED_KEYS.has(key)) {
    // Programmer-error guard. Niet stilletjes doorgaan met onbekende key.
    // eslint-disable-next-line no-console
    console.warn(`useUiPreference: onbekende key "${key}"`);
  }

  const hasAuth = Boolean(auth);
  const prefs = (user && user.ui_preferences) || {};
  const value = hasAuth ? (prefs[key] || defaultValue) : localView;

  // CRITICAL #3 — adversarial review: zonder AdminAuthProvider gaat de hook
  // stilletjes in memory-only modus. Dat is OK voor unit-tests, maar in
  // productie zou een vergeten provider verborgen blijven (admin merkt geen
  // persistentie maar krijgt ook geen error). Warn één keer per key zodat
  // het in dev-tools zichtbaar is zonder dat het de UI crasht.
  // In test-omgeving (NODE_ENV === "test") suppressen we de warning om
  // existing unit-tests die zonder provider renderen niet te vervuilen.
  useEffect(() => {
    if (hasAuth) return;
    if (typeof process !== "undefined" && process.env && process.env.NODE_ENV === "test") {
      return;
    }
    if (_missingContextWarned.has(key)) return;
    _missingContextWarned.add(key);
    // eslint-disable-next-line no-console
    console.warn(
      `[useUiPreference] AdminAuthContext missing — preferences for "${key}" will not persist. ` +
        "Fallback is bedoeld voor unit-tests; productie moet altijd binnen <AdminAuthProvider> renderen.",
    );
  }, [hasAuth, key]);

  // CRITICAL #2 — adversarial review: setValue had `value` in deps en deed
  // `if (next === value) return;`. Bij snelle dubbel-klik kon een stale
  // closure de tweede click silently no-oppen. Fix: we lezen current-state
  // via refs i.p.v. via captured `value`, en we halen `value`/`prefs` uit de
  // dep-array. setValue is daarmee identiteit-stabiel tussen renders en de
  // no-op-check kijkt altijd naar de meest recente state.
  const prefsRef = useRef(prefs);
  const localViewRef = useRef(localView);
  const hasAuthRef = useRef(hasAuth);
  const userRef = useRef(user);
  const updateLocalRef = useRef(updateLocalUserPreferences);
  const defaultRef = useRef(defaultValue);

  useEffect(() => { prefsRef.current = prefs; });
  useEffect(() => { localViewRef.current = localView; }, [localView]);
  useEffect(() => { hasAuthRef.current = hasAuth; }, [hasAuth]);
  useEffect(() => { userRef.current = user; }, [user]);
  useEffect(() => { updateLocalRef.current = updateLocalUserPreferences; }, [updateLocalUserPreferences]);
  useEffect(() => { defaultRef.current = defaultValue; }, [defaultValue]);

  const setValue = useCallback(
    async (next) => {
      if (!ALLOWED_VALUES.has(next)) {
        // eslint-disable-next-line no-console
        console.warn(`useUiPreference: ongeldige waarde "${next}" voor key "${key}"`);
        return;
      }

      const currentHasAuth = hasAuthRef.current;
      const currentPrefs = prefsRef.current || {};
      const currentValue = currentHasAuth
        ? (currentPrefs[key] || defaultRef.current)
        : localViewRef.current;

      if (next === currentValue) return; // no-op tegen huidige (live) state

      // Geen auth-context → memory-only fallback (unit-tests, storybook, etc.)
      if (!currentHasAuth) {
        setLocalView(next);
        return;
      }

      const currentUser = userRef.current;
      const updateLocal = updateLocalRef.current;
      // Geen ingelogde user (bootstrap of net uitgelogd) → niets te persisteren.
      if (!currentUser || !updateLocal) return;

      const previousPrefs = { ...currentPrefs };
      const optimistic = { ...currentPrefs, [key]: next };
      updateLocal(optimistic);

      try {
        const res = await adminPatch("/api/auth/admin/me/preferences", { [key]: next });
        // Server is bron van waarheid — sync exact wat de server bewaarde
        if (res && res.ui_preferences && typeof res.ui_preferences === "object") {
          // updateLocalRef pakken zodat we de meest recente referentie gebruiken
          updateLocalRef.current?.(res.ui_preferences);
        }
      } catch (err) {
        updateLocalRef.current?.(previousPrefs);
        toast.error(`Weergave-keuze niet opgeslagen: ${formatApiError(err)}`);
      }
    },
    [key], // OPZETTELIJK alleen `key`: voorkomt stale-closure bij rapid clicks
  );

  return [value, setValue];
}
